import React, {useMemo} from 'react';
import PropTypes from "prop-types";
import {useFormik} from "formik";

import BookingService from "../../../../../services/bookings";
import {ResponsiveDialog} from "../../../../../components/ResponsiveDialog";

import paymentValidationSchema from "./validator";

import {DatePicker} from "@mui/x-date-pickers";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField/TextField";
import {usePaymentCreate} from "../../../query";
import {useFeedbackTools} from "../../../../../components/FeedbackTools";


const FormPayment = (props) => {
    const {booking, transactionType, initialAmount, initialReasonOfPayment, onClose} = props;
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();
    const paymentCreate = usePaymentCreate({bookingId: booking.id});

    const formik = useFormik({
        initialValues: {
            date: new Date(),
            paymentType: '',
            reasonOfPayment: initialReasonOfPayment || '',
            amount: initialAmount ?? null,
        },
        validationSchema: paymentValidationSchema,
        onSubmit: async (values) => {
            if (transactionType === 'outgoing') {
                values.amount = values.amount * -1;
            }
            try {
                await paymentCreate.mutateAsync({data: values});
                onClose();
                showSuccessNotification('Pagamento creato con successo');
            } catch (e) {
                showErrorNotification(e);
            }
        }
    });

    const paymentsReasons = useMemo(
        () => BookingService.enums.PAYMENTS_REASONS.filter(r => r.transactionType === transactionType),
        []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitIsEnabled = !Object.keys(formik.errors).some(x => formik.touched[x]);

    return (
        <ResponsiveDialog title={transactionType === 'incoming' ? "Nuovo pagamento" : "Nuovo rimborso"}
                          isOpen={true}
                          maxWidth="md"
                          scroll="paper"
                          isLoading={paymentCreate.isLoading}
                          showApply={true}
                          applyLabel="Salva"
                          enableApply={submitIsEnabled}
                          onApply={formik.handleSubmit}
                          closeLabel="Cancella"
                          onClose={onClose}>
            <Grid container spacing={3} sx={{pt: 1}}>
                <Grid item xs={12} md={3}>
                    <DatePicker value={formik.values.date}
                                onChange={date => formik.setFieldValue("date", date)}
                                disableFuture
                                label="Data pagamento"
                                closeOnSelect
                                disableHighlightToday
                                showDaysOutsideCurrentMonth
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: Boolean(formik.touched.date) && Boolean(formik.errors.date),
                                        helperText: formik.touched.date && formik.errors.date,
                                    }
                                }}/>
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField select
                               fullWidth
                               name="paymentType"
                               label="Modalità pagamento"
                               value={formik.values.paymentType}
                               onChange={formik.handleChange}
                               error={formik.touched.paymentType && Boolean(formik.errors.paymentType)}
                               helperText={formik.touched.paymentType && formik.errors.paymentType}>
                        {
                            BookingService.enums.PAYMENTS_TYPES.map(t => (
                                <MenuItem key={t.value} value={t.value}>
                                    {t.label}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField select
                               fullWidth
                               name="reasonOfPayment"
                               label="Causale"
                               value={formik.values.reasonOfPayment}
                               onChange={formik.handleChange}
                               error={formik.touched.reasonOfPayment && Boolean(formik.errors.reasonOfPayment)}
                               helperText={formik.touched.reasonOfPayment && formik.errors.reasonOfPayment}>
                        {
                            paymentsReasons.map(r => (
                                <MenuItem key={r.value} value={r.value}>
                                    {r.label}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField type="number"
                               label="Importo"
                               fullWidth
                               name="amount"
                               value={formik.values.amount ?? ''}
                               onChange={formik.handleChange}
                               error={formik.touched.amount && Boolean(formik.errors.amount)}
                               helperText={formik.touched.amount && formik.errors.amount}
                               InputProps={{
                                   startAdornment: <InputAdornment position="start">€</InputAdornment>,
                               }}/>
                </Grid>
            </Grid>
        </ResponsiveDialog>
    );
};

FormPayment.propTypes = {
    transactionType: PropTypes.oneOf(['incoming', 'outgoing']).isRequired,
    initialAmount: PropTypes.number,
    initialReasonOfPayment: PropTypes.string,
    onClose: PropTypes.func.isRequired,
}

export default FormPayment;